import { DEFAULT_MIN_ENROLLMENT, defaultBreakpoints, defaultWeights } from './indicatorConfig';

function parseLevel(searchParams, dataset) {
  const levelIds = Object.keys(dataset.levels);
  const requested = searchParams.get('level');
  return levelIds.includes(requested) ? requested : levelIds[0];
}

function nearlyEqual(a, b) {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) < 1e-9;
}

export function parseCalculatorSearch(searchParams, dataset) {
  const level = parseLevel(searchParams, dataset);
  const indicators = dataset.levels[level].indicators;
  // An absent `min` must fall through to the workbook default. Number(null) is
  // 0, which is a legitimate value, so the param has to be presence-checked.
  const minRaw = searchParams.get('min');
  const minParsed = minRaw === null ? NaN : Number(minRaw);
  const minEnrollment =
    Number.isFinite(minParsed) && minParsed >= 0 ? minParsed : DEFAULT_MIN_ENROLLMENT;

  const weights = defaultWeights(indicators);
  const encodedWeights = searchParams.get('w');
  if (encodedWeights) {
    const parts = encodedWeights.split(',');
    if (parts.length === indicators.length) {
      indicators.forEach((indicator, index) => {
        const parsed = Number(parts[index]);
        weights[indicator.id] = Number.isFinite(parsed) ? parsed : indicator.defaultWeight;
      });
    }
  }

  const breakpointsById = defaultBreakpoints(indicators);
  const encodedBreakpoints = searchParams.get('bp');
  if (encodedBreakpoints) {
    encodedBreakpoints.split(';').forEach((chunk) => {
      const [id, rest] = chunk.split(':');
      if (!id || !rest) return;
      const values = rest.split('|').map(Number);
      if (values.length === 4 && values.every((value) => Number.isFinite(value))) {
        breakpointsById[id] = values;
      }
    });
  }

  return {
    level,
    minEnrollment,
    renormalize: searchParams.get('renorm') === '1',
    autoNormalize: searchParams.get('autonorm') !== '0',
    weights,
    breakpointsById,
  };
}

export function serializeCalculatorSearch({
  level,
  indicators,
  defaultLevel,
  minEnrollment,
  renormalize,
  autoNormalize,
  weights,
  breakpointsById,
}) {
  const params = new URLSearchParams();
  if (defaultLevel && level !== defaultLevel) params.set('level', level);
  if (minEnrollment !== DEFAULT_MIN_ENROLLMENT) params.set('min', String(minEnrollment));
  if (renormalize) params.set('renorm', '1');
  if (!autoNormalize) params.set('autonorm', '0');

  const defaults = defaultWeights(indicators);
  const weightsDiffer = indicators.some(
    (indicator) => !nearlyEqual(weights[indicator.id], defaults[indicator.id])
  );
  if (weightsDiffer) {
    params.set('w', indicators.map((indicator) => String(weights[indicator.id] ?? 0)).join(','));
  }

  const defaultBp = defaultBreakpoints(indicators);
  const bpParts = indicators
    .filter((indicator) => {
      const current = breakpointsById[indicator.id];
      const fallback = defaultBp[indicator.id];
      return current && fallback && current.some((value, index) => !nearlyEqual(value, fallback[index]));
    })
    .map((indicator) => `${indicator.id}:${breakpointsById[indicator.id].join('|')}`);
  if (bpParts.length) params.set('bp', bpParts.join(';'));

  return params;
}
